import { CommonModule, Location } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { HttpService } from '../../services/http.service';
import { InputComponent } from '../from-input/input.component';
import { CustomToastComponent } from '../toast/custom-toast.component';
import { MatDialog } from '@angular/material/dialog';
import { ConsentSuccessDialogComponent } from '../dialog/consent-success/consent-success-dialog.component';
import { AlertDialogComponent, AlertDialogData } from '../dialog/alert-dialog/alert-dialog.component';

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: {
    token: string | null;
    status: string;
    studentFirstName?: string;
    studentLastName?: string;
    dateOfBirth?: string | Date;
    schoolName?: string;
    schoolAddress?: string;
    schoolEmail?: string;
    eventStartDate?: string | Date;
    requiresMediaDecision?: boolean;
    parentApprovalStatus?: string;
    mediaConsentStatus?: string;
    accountUpdatedBy?: string;
    mediaConsentUpdatedBy?: string;
  };
}

@Component({
  selector: 'app-consent-form',
  templateUrl: './consent-form.component.html',
  styleUrls: ['./consent-form.component.scss'],
  imports: [ReactiveFormsModule, CommonModule, FormsModule, InputComponent],
})
export class ConsentFormComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private token: string | null = null;
  @Input() isStudent: boolean = false;
  @Input() data: any = null;
  @Input() mediaToken: string = this.data?.mediaConsentToken
    ? this.data?.mediaConsentToken
    : '';
  @Output() consentSubmitted = new EventEmitter<void>();

  approvalStatus: boolean | null = null;
  isAlreadyApproved: boolean = false;
  isLoading: boolean = false;
  form!: FormGroup;
  studentData: any = null;
  isStudent16: boolean = false;
  requiresMediaDecision: boolean = true;
  hasSubmittedDecision: boolean = false;
  customErrorMessages = {
    audienceTypeName: { required: 'Please enter a audience name.' },
  };
  dataOption: any[] = [
    { label: 'Approve', value: 'approve' },
    { label: 'Decline', value: 'decline' },
  ];
  isSubmitted: boolean = false;

  constructor(
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private location: Location,
    public router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.token = params['token'] || null;
    });
    this.initializeForm();
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private disableFormAfterSubmit(): void {
    this.form.get('approval')?.disable();
    this.form.get('secondapproval')?.disable();
    this.hasSubmittedDecision = true;
  }

  private initializeForm(): void {
    this.form = new FormGroup({
      studentAge: new FormControl({ value: '', disabled: true }, [
        Validators.required,
      ]),
      approval: new FormControl(''),
      secondapproval: new FormControl(''),
    });

    this.form
      .get('approval')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncParentMediaConsentState();
        this.syncFormValidators();
      });

    this.syncFormValidators();
  }

  private initializeComponent(): void {
    if (this.data) {
      this.studentData = this.data;
      this.token = this.data?.mediaConsentToken || this.mediaToken;
      this.setStudentAge();
    } else if (this.mediaToken || this.data?.mediaConsentToken) {
      this.fetchEventDetails();
    } else {
      this.showErrorToast('Unable to load consent form data');
    }
  }

  private calculateAge(dateOfBirth: string | Date): number {
    if (!dateOfBirth) return 0;

    const birthDate = new Date(dateOfBirth);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  }
  private mapStatusToApproval(status: string | undefined | null): string {
    if (status === 'approved') return 'approve';
    if (status === 'declined' || status === 'rejected') return 'decline';
    return '';
  }

  private setStudentAge(): void {
    if (this.studentData?.dateOfBirth) {
      const age = this.calculateAge(this.studentData.dateOfBirth);
      this.isStudent16 = age >= 16;
      this.form.get('studentAge')?.setValue(age.toString());
    }

    if (!this.isStudent) {
      this.requiresMediaDecision =
        this.studentData?.requiresMediaDecision ?? !this.isStudent16;
    }

    this.syncFormValidators();
    this.applyExistingDecisions();
  }

  private fetchEventDetails(): void {
    if (this.data?.mediaConsentToken || this.mediaToken) {
      this.isLoading = true;
      this.httpService
        .getWithoutAuth<ApiDataResponse>(
          `students/parent-approval/details?token=${this.mediaToken}`,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response?.status) {
              this.studentData = response.data;
              this.token = response.data?.token || this.mediaToken;
              this.setStudentAge();
            } else {
              this.showErrorToast(response.message);
            }
            this.isLoading = false;
          },
          error: (error) => {
            this.showErrorToast(error?.message);
            this.isLoading = false;
          },
        });
    }
  }

  private getApprovalValue(controlName: string): boolean | null {
    const value = this.form.get(controlName)?.value;
    if (value === 'approve') return true;
    if (value === 'decline') return false;
    return null;
  }

  updateApprovalStatus(showSuccess: boolean = false): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    if (!this.token) {
      this.showErrorToast('Token is required');
      return;
    }

    const accountApproved = this.showAccountApproval
      ? this.getApprovalValue('approval')
      : null;
    let mediaApproved = this.showMediaApproval
      ? this.getApprovalValue('secondapproval')
      : null;

    if (this.showAccountApproval && accountApproved === null) {
      return;
    }

    if (this.requiresExplicitMediaApproval && mediaApproved === null) {
      return;
    }

    const dialogData: AlertDialogData = {
      title: 'Confirm Submission',
      message: 'Are you sure you want to continue with the submission?',
      submessage: '',
      okText: 'Confirm',
      cancelText: 'Cancel',
      icon: 'warning',
      okButtonColor: '#5BD763',
      cancelButtonColor: '#EF4A00',
    };

    const confirmRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
      disableClose: true,
    });

    confirmRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.submitConsentPayload({ token: this.token!, accountApproved, mediaApproved }, showSuccess);
    });
  }

  private submitConsentPayload(
    payload: { token: string; accountApproved: boolean | null; mediaApproved: boolean | null },
    showSuccess: boolean
  ): void {
    this.isLoading = true;
    this.httpService
      .post<ApiDataResponse>('students/parent-approval/respond', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.status) {
            this.fetchEventDetails();
            this.disableFormAfterSubmit();
            if (showSuccess) {
              this.isSubmitted = true;
              this.dialog.open(ConsentSuccessDialogComponent, {
                data: {},
                disableClose: true,
                width: '788px',
                maxWidth: '788px',
                panelClass: 'consent-success-dialog',
              });
            }
          }
          this.isLoading = false;
          this.consentSubmitted.emit();
        },
        error: (err) => {
          this.showErrorToast(err?.message);
          this.isLoading = false;
        },
      });
  }

  private showSuccessToast(message: string): void {
    this.showToast(message, 'success-toast');
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.showToast(message, 'error-toast');
  }

  private showToast(message: string, panelClass: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', panelClass],
    });
  }

  get showAccountApproval(): boolean {
    return this.isStudent ? !this.isStudent16 : true;
  }

  get showMediaApproval(): boolean {
    return this.isStudent ? true : this.requiresMediaDecision;
  }

  get isMediaConsentSelectionDisabled(): boolean {
    if (!this.showMediaApproval) {
      return false;
    }

    const mediaStatus = (this.studentData?.mediaConsentStatus || '').toLowerCase();
    if (mediaStatus === 'approved' || this.isDeclinedStatus(mediaStatus)) {
      return true;
    }

    if (this.isStudent) {
      return !this.isApprovedStatus(this.studentData?.parentApprovalStatus);
    }

    return !this.form.get('approval')?.value;
  }

  get isMediaConsentWaitingForAccountSelection(): boolean {
    if (!this.showMediaApproval) {
      return false;
    }

    if (this.isStudent) {
      return !this.isApprovedStatus(this.studentData?.parentApprovalStatus);
    }

    return !this.form.get('approval')?.value;
  }

  get shouldAutoDeclineMediaConsent(): boolean {
    if (this.isStudent || !this.showMediaApproval) {
      return false;
    }

    return this.form.get('approval')?.value === 'decline';
  }

  get requiresExplicitMediaApproval(): boolean {
    if (!this.showMediaApproval) {
      return false;
    }

    if (this.isStudent) {
      return this.isApprovedStatus(this.studentData?.parentApprovalStatus);
    }

    return this.form.get('approval')?.value === 'approve';
  }

  private isApprovedStatus(status: string | undefined | null): boolean {
    return (status || '').toLowerCase() === 'approved';
  }

  private isDeclinedStatus(status: string): boolean {
    return status === 'declined' || status === 'rejected';
  }

  private syncParentMediaConsentState(): void {
    const mediaControl = this.form.get('secondapproval');

    if (!mediaControl || this.isStudent || !this.showMediaApproval) {
      return;
    }

    const mediaStatus = (this.studentData?.mediaConsentStatus || '').toLowerCase();
    const hasMediaDecision =
      mediaStatus === 'approved' || this.isDeclinedStatus(mediaStatus);

    if (hasMediaDecision || this.hasSubmittedDecision) {
      return;
    }

    const approvalValue = this.form.get('approval')?.value;

    if (approvalValue === 'decline') {
      mediaControl.enable({ emitEvent: false });
      return;
    }

    if (approvalValue === 'approve') {
      mediaControl.enable({ emitEvent: false });
      if (mediaControl.value === 'decline') {
        mediaControl.setValue('', { emitEvent: false });
      }
      return;
    }

    mediaControl.setValue('', { emitEvent: false });
    mediaControl.disable({ emitEvent: false });
  }

  private syncFormValidators(): void {
    const approvalControl = this.form.get('approval');
    const mediaControl = this.form.get('secondapproval');

    if (this.showAccountApproval) {
      approvalControl?.setValidators([Validators.required]);
    } else {
      approvalControl?.clearValidators();
    }

    if (this.requiresExplicitMediaApproval) {
      mediaControl?.setValidators([Validators.required]);
    } else {
      mediaControl?.clearValidators();
    }

    approvalControl?.updateValueAndValidity({ emitEvent: false });
    mediaControl?.updateValueAndValidity({ emitEvent: false });
  }

  private applyExistingDecisions(): void {
    const approvalControl = this.form.get('approval');
    const mediaControl = this.form.get('secondapproval');
    const parentStatus = (
      this.studentData?.parentApprovalStatus || ''
    ).toLowerCase();
    const mediaStatus = (
      this.studentData?.mediaConsentStatus || ''
    ).toLowerCase();

    const hasAccountDecision =
      parentStatus === 'approved' || this.isDeclinedStatus(parentStatus);
    const hasMediaDecision =
      mediaStatus === 'approved' || this.isDeclinedStatus(mediaStatus);

    if (hasAccountDecision) {
      approvalControl?.setValue(this.mapStatusToApproval(parentStatus), {
        emitEvent: false,
      });
      approvalControl?.disable({ emitEvent: false });
    } else {
      approvalControl?.enable({ emitEvent: false });
    }

    if (hasMediaDecision) {
      mediaControl?.setValue(this.mapStatusToApproval(mediaStatus), {
        emitEvent: false,
      });
      mediaControl?.disable({ emitEvent: false });
    }

    this.syncParentMediaConsentState();
    this.syncFormValidators();

    const accountFinalized = !this.showAccountApproval || hasAccountDecision;
    const mediaFinalized = !this.showMediaApproval || hasMediaDecision;

    this.hasSubmittedDecision = accountFinalized && mediaFinalized;
  }
}
